/**
 * Validador de Configuração do SDK
 * Responsável por validar e normalizar configurações de entrada
 */

export class ConfigValidator {
    /**
     * Configuração padrão do SDK
     */
    static get DEFAULT_CONFIG() {
        return {
            apiUrl: '/api/v1/fingerprint',
            customerId: null,
            debug: false,
            timeout: 10000,
            retries: 3,
            collectBehavioral: true,
            behavioralTimeout: 30000,
            canvasEnabled: true,
            webglEnabled: true,
            audioEnabled: true,
            privacyDetectionEnabled: true,
            networkAnalysisEnabled: true,
            maxBehavioralEvents: {
                mouse: 100,
                keyboard: 50,
                touch: 50
            },
            performance: {
                enableMetrics: true,
                sampleRate: 1.0
            },
            autoGenerate: true,
            autoSend: true
        };
    }

    /**
     * Regras de validação
     */
    static get VALIDATION_RULES() {
        return {
            apiUrl: {
                type: 'string',
                required: true,
                validator: (value) => {
                    try {
                        new URL(value, window.location.origin);
                        return true;
                    } catch {
                        return false;
                    }
                },
                message: 'apiUrl deve ser uma URL válida'
            },
            customerId: {
                type: ['number', 'string', 'null'],
                validator: (value) => {
                    if (value === null) return true;
                    if (typeof value === 'number') return value > 0;
                    if (typeof value === 'string') return value.length > 0;
                    return false;
                },
                message: 'customerId deve ser um número positivo, string não vazia ou null'
            },
            debug: {
                type: 'boolean',
                message: 'debug deve ser um boolean'
            },
            timeout: {
                type: 'number',
                validator: (value) => value > 0 && value <= 60000,
                message: 'timeout deve ser entre 1 e 60000ms'
            },
            retries: {
                type: 'number',
                validator: (value) => value >= 0 && value <= 10,
                message: 'retries deve ser entre 0 e 10'
            },
            collectBehavioral: {
                type: 'boolean',
                message: 'collectBehavioral deve ser um boolean'
            },
            behavioralTimeout: {
                type: 'number',
                validator: (value) => value > 0 && value <= 300000,
                message: 'behavioralTimeout deve ser entre 1 e 300000ms'
            },
            canvasEnabled: {
                type: 'boolean',
                message: 'canvasEnabled deve ser um boolean'
            },
            webglEnabled: {
                type: 'boolean',
                message: 'webglEnabled deve ser um boolean'
            },
            audioEnabled: {
                type: 'boolean',
                message: 'audioEnabled deve ser um boolean'
            },
            privacyDetectionEnabled: {
                type: 'boolean',
                message: 'privacyDetectionEnabled deve ser um boolean'
            },
            networkAnalysisEnabled: {
                type: 'boolean',
                message: 'networkAnalysisEnabled deve ser um boolean'
            }
        };
    }

    /**
     * Valida e normaliza configuração
     */
    static validate(config = {}) {
        // Obter configuração global injetada pelo backend
        const globalConfig = (typeof window !== 'undefined' && window.FingerprintSDKConfig) || {};

        // Mescla com configuração padrão, global e do usuário
        // Prioridade: Usuário > Global > Padrão
        const mergedConfig = { ...this.DEFAULT_CONFIG, ...globalConfig, ...config };

        // Valida cada campo
        const errors = [];

        for (const [key, rule] of Object.entries(this.VALIDATION_RULES)) {
            const value = mergedConfig[key];
            const error = this.validateField(key, value, rule);

            if (error) {
                errors.push(error);
            }
        }

        // Validações especiais
        const specialErrors = this.validateSpecialRules(mergedConfig);
        errors.push(...specialErrors);

        if (errors.length > 0) {
            throw new Error(`Configuração inválida: ${errors.join(', ')}`);
        }

        return this.normalizeConfig(mergedConfig);
    }

    /**
     * Valida um campo específico
     */
    static validateField(key, value, rule) {
        // Verifica tipo
        const types = Array.isArray(rule.type) ? rule.type : [rule.type];
        const valueType = value === null ? 'null' : typeof value;

        if (!types.includes(valueType)) {
            return `${key}: esperado ${types.join(' ou ')}, recebido ${valueType}`;
        }

        // Verifica validador customizado
        if (rule.validator && !rule.validator(value)) {
            return `${key}: ${rule.message}`;
        }

        return null;
    }

    /**
     * Validações especiais para objetos aninhados
     */
    static validateSpecialRules(config) {
        const errors = [];

        // Valida maxBehavioralEvents
        if (config.maxBehavioralEvents) {
            const events = config.maxBehavioralEvents;

            if (typeof events !== 'object' || events === null) {
                errors.push('maxBehavioralEvents deve ser um objeto');
            } else {
                ['mouse', 'keyboard', 'touch'].forEach(type => {
                    if (events[type] !== undefined) {
                        if (typeof events[type] !== 'number' || events[type] < 0 || events[type] > 1000) {
                            errors.push(`maxBehavioralEvents.${type} deve ser um número entre 0 e 1000`);
                        }
                    }
                });
            }
        }

        // Valida performance
        if (config.performance) {
            const perf = config.performance;

            if (typeof perf !== 'object' || perf === null) {
                errors.push('performance deve ser um objeto');
            } else {
                if (perf.enableMetrics !== undefined && typeof perf.enableMetrics !== 'boolean') {
                    errors.push('performance.enableMetrics deve ser um boolean');
                }

                if (perf.sampleRate !== undefined) {
                    if (typeof perf.sampleRate !== 'number' || perf.sampleRate < 0 || perf.sampleRate > 1) {
                        errors.push('performance.sampleRate deve ser um número entre 0 e 1');
                    }
                }
            }
        }

        return errors;
    }

    /**
     * Normaliza configuração (conversões de tipo, etc.)
     */
    static normalizeConfig(config) {
        const normalized = { ...config };

        // Converte customerId para string se for número
        if (typeof normalized.customerId === 'number') {
            normalized.customerId = normalized.customerId.toString();
        }

        // Garante que URLs terminam sem barra
        if (normalized.apiUrl.endsWith('/')) {
            normalized.apiUrl = normalized.apiUrl.slice(0, -1);
        }

        // Mescla objetos aninhados com padrões
        normalized.maxBehavioralEvents = {
            ...this.DEFAULT_CONFIG.maxBehavioralEvents,
            ...normalized.maxBehavioralEvents
        };

        normalized.performance = {
            ...this.DEFAULT_CONFIG.performance,
            ...normalized.performance
        };

        return normalized;
    }

    /**
     * Valida configuração parcial (para updates)
     */
    static validatePartial(partialConfig) {
        const errors = [];

        for (const [key, value] of Object.entries(partialConfig)) {
            const rule = this.VALIDATION_RULES[key];

            if (!rule) {
                errors.push(`Campo desconhecido: ${key}`);
                continue;
            }

            const error = this.validateField(key, value, rule);
            if (error) {
                errors.push(error);
            }
        }

        if (errors.length > 0) {
            throw new Error(`Configuração parcial inválida: ${errors.join(', ')}`);
        }

        return true;
    }

    /**
     * Obtém configuração padrão
     */
    static getDefault() {
        return { ...this.DEFAULT_CONFIG };
    }

    /**
     * Verifica se uma configuração é válida sem lançar erro
     */
    static isValid(config) {
        try {
            this.validate(config);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Obtém lista de campos obrigatórios
     */
    static getRequiredFields() {
        return Object.entries(this.VALIDATION_RULES)
            .filter(([, rule]) => rule.required)
            .map(([key]) => key);
    }

    /**
     * Obtém documentação dos campos
     */
    static getFieldDocumentation() {
        const docs = {};

        for (const [key, rule] of Object.entries(this.VALIDATION_RULES)) {
            docs[key] = {
                type: rule.type,
                required: rule.required || false,
                description: rule.message,
                default: this.DEFAULT_CONFIG[key]
            };
        }

        return docs;
    }
}